
import React from 'react';

interface ModernDialogProps {
  isOpen: boolean;
  type: 'danger' | 'warning' | 'info';
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
  onConfirm: () => void;
  onCancel: () => void;
  icon?: string;
}

const ModernDialog: React.FC<ModernDialogProps> = ({
  isOpen,
  type,
  title,
  message,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  icon
}) => {
  if (!isOpen) return null;

  const themes = {
    danger: {
      bg: 'bg-rose-500',
      text: 'text-rose-500',
      lightBg: 'bg-rose-500/10',
      shadow: 'shadow-rose-500/20',
      icon: icon || 'fa-trash-can'
    },
    warning: {
      bg: 'bg-amber-500',
      text: 'text-amber-500',
      lightBg: 'bg-amber-500/10',
      shadow: 'shadow-amber-500/20',
      icon: icon || 'fa-exclamation-triangle'
    },
    info: {
      bg: 'bg-emerald-500',
      text: 'text-emerald-500',
      lightBg: 'bg-emerald-500/10',
      shadow: 'shadow-emerald-500/20',
      icon: icon || 'fa-info-circle'
    }
  };

  const currentTheme = themes[type];

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 lg:p-6 animate-in fade-in duration-300">
      <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-md" onClick={onCancel} />
      
      <div className="relative w-full max-w-sm bg-white dark:bg-[#111115] rounded-[40px] shadow-2xl overflow-hidden border border-white/10 animate-in zoom-in-95 duration-300">
        <div className="p-8 flex flex-col items-center text-center">
          <div className={`w-20 h-20 ${currentTheme.lightBg} ${currentTheme.text} rounded-[28px] flex items-center justify-center text-3xl mb-6 shadow-xl`}>
            <i className={`fas ${currentTheme.icon}`}></i>
          </div>
          
          <h3 className="text-xl font-black text-slate-800 dark:text-white mb-3">{title}</h3>
          <p className="text-sm font-medium text-slate-500 dark:text-slate-400 leading-relaxed px-2 mb-8">
            {message}
          </p>
          
          <div className="flex flex-col w-full gap-3">
            <button 
              onClick={onConfirm}
              className={`w-full py-4 rounded-2xl ${currentTheme.bg} text-white font-black text-sm shadow-lg ${currentTheme.shadow} hover:opacity-90 active:scale-[0.98] transition-all`}
            >
              {confirmText}
            </button>
            <button 
              onClick={onCancel}
              className="w-full py-4 rounded-2xl bg-slate-100 dark:bg-white/5 text-slate-500 dark:text-slate-400 font-black text-sm hover:bg-slate-200 dark:hover:bg-white/10 transition-all"
            >
              {cancelText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModernDialog;
